import classnames from 'classnames';
import { isNil } from 'ramda';
import videojs, { VideoJsPlayer } from 'video.js';

import type QualityLevelsManagerPlugin from '@src/plugins/QualityLevelsManagerPlugin';
import { Event } from '@src/types/event';

export const Component = videojs.getComponent('Component');

type ComponentOptions = videojs.ComponentOptions;

class ToastDisplay extends Component {
  private _message = '';
  private _intervalId: number | null = null;
  private _activeTextTrackId: undefined | string | null = undefined;

  constructor(
    player: VideoJsPlayer,
    options?: ComponentOptions,
    ready?: videojs.Component.ReadyCallback
  ) {
    super(player, options, ready);
    this.updateDomContent();
    this.handleTextTracksByClick();
    /*
    this.handleTextTracksByChange();
    */
    this.handleQualityLevels();
  }

  handleTextTracksByClick() {
    this.player_.on(Event.X_CLICK_SUBTITLE, (evt, data) => {
      const isSelectDisableTextTrack = data?.label === 'subtitles off';

      if (!isSelectDisableTextTrack) {
        this.toast(`已選擇${this.localize(data?.label ?? '')}字幕`);
      } else {
        this.toast(`已關閉字幕`);
      }
    });
  }

  /*
  handleTextTracksByChange() {
    const textTracks = this.player_.textTracks();

    textTracks.addEventListener('change', () => {
      if (this.player_.ads?.inAdBreak?.()) {
        return;
      }

      let activeTextTrack;
      let isAllTracksDisabled = false;

      for (let i = 0; i < textTracks.length; i++) {
        const track = textTracks[i];
        if (track.kind !== 'subtitles') continue;

        isAllTracksDisabled = track.mode === 'disabled';

        if (track.mode === 'showing') {
          activeTextTrack = track;
          break;
        }
      }

      if (
        this._activeTextTrackId !== undefined &&
        activeTextTrack?.id !== this._activeTextTrackId
      ) {
        if (activeTextTrack) {
          this.toast(`已選擇${this.localize(activeTextTrack.label)}字幕`);
        } else if (isAllTracksDisabled) {
          this.toast(`已關閉字幕`);
        }
      }

      // set fallback as null so the for loop logic above in next event should not skip toast
      this._activeTextTrackId = activeTextTrack?.id ?? null;
    });
  }
  */

  handleQualityLevels() {
    const setupSelectQualityLevelHandler = () => {
      const qualityLevelsManagerPlugin = this.player_
        .qualityLevelsManagerPlugin as QualityLevelsManagerPlugin | undefined;

      // TODO cleanup on component dispose
      this.player_.on(Event.X_CLICK_QUALITYLEVEL, (evt, data) => {
        qualityLevelsManagerPlugin?.getQualityLevelList()?.levels_ || [];

        this.toast(`已${data.label ? `選擇${data.label}` : '調整'}畫質`);
      });
    };

    if (!this.player_.usingPlugin('qualityLevelsManagerPlugin')) {
      // TODO cleanup on component dispose
      this.player_.on(Event.X_PLUGININIT_QUALITYLEVELSMANAGERPLUGIN, () => {
        setupSelectQualityLevelHandler();
      });
      return;
    }

    setupSelectQualityLevelHandler();
  }

  createEl(tag = 'div', props = {}, attributes = {}) {
    props = {
      ...props,
      className: this.buildCSSClass(),
    };

    return videojs.dom.createEl(tag, props, attributes);
  }

  buildCSSClass() {
    return classnames(super.buildCSSClass(), 'vjs-toast-display');
  }

  clearIntervalId() {
    if (!isNil(this._intervalId)) {
      window.clearTimeout(this._intervalId);
      this._intervalId = null;
    }
  }

  show(options?: { duration?: number }) {
    this.el().classList.add('vjs-toast-display--show');

    this.clearIntervalId();

    if (
      typeof options?.duration === 'number' &&
      options.duration !== Number.POSITIVE_INFINITY
    ) {
      this._intervalId = window.setTimeout(
        this.hide.bind(this),
        options.duration
      );
    }
  }

  hide() {
    this.clearIntervalId();
    this.el().classList.remove('vjs-toast-display--show');
  }

  toast(
    message: string,
    options?: {
      duration?: number;
    }
  ) {
    const { duration = 2500 } = options ?? {};
    this.updateMessage(message);
    this.show({ duration });
  }

  updateMessage(message: string) {
    this._message = message;
    this.updateDomContent();
  }

  updateDomContent() {
    videojs.dom.emptyEl(this.el());

    const messageEl = videojs.dom.createEl('span', {
      className: 'vjs-toast-display__message',
    });

    messageEl.append(document.createTextNode(this._message || ''));

    this.el().appendChild(messageEl);
  }
}

videojs.registerComponent('ToastDisplay', ToastDisplay);

export default ToastDisplay;
